import { IState, Status } from "./state";
import { ActionTree, Store, CommitOptions, Dispatch } from 'vuex';
import * as api from '../api';

type PayloadType = 'initApp' | 'setState' | 'subscribe' | 'setLoading' | 'unsubscribe';
export type ActionPayload<ActionData> = {
	type: PayloadType,
	payload: ActionData
}

export interface Commit<P> {
(type: PayloadType, payload?: any, options?: CommitOptions): void;
(payloadWithType: P, options?: CommitOptions): void;
}

export interface ActionContext<S, R, P = ActionPayload<any>> {
	dispatch: Dispatch;
	commit: Commit<P>;
	state: S;
	getters: any;
	rootState: R;
	rootGetters: any;
}

type ActionHandler<ActionData> = (this: Store<IState>, injectee: ActionContext<IState, IState>, payload: ActionPayload<ActionData>) => any;
type ActionCreator<ActionData> = (data: ActionData) => ActionPayload<ActionData>;

// Payloads
export interface ISubscribePayload {
	email: string;
	keywords: string[];
	solution: string;
}

// Action creators
export const initApp: ActionCreator<IState> = (state) => ({
	type: 'initApp',
	payload: state
})

export const subscribe: ActionCreator<ISubscribePayload> = payload => ({
	type: 'subscribe',
	payload
})

export const unsubscribe: ActionCreator<any> = () => ({
	type: 'unsubscribe',
	payload: undefined
})

// Action handlers
const initAppAction: ActionHandler<IState> = ({ commit }, { payload }) => {
	commit({
		type: 'setState',
		payload: {
			...payload,
			status: {
				...payload.status,
				messages: Object.keys(payload.status.messages).reduce((messages: any, key) => {
					messages[parseInt(key)] = (payload.status.messages as any)[key];
					return messages;
				}, {})
			}
		}
	})
}

const subscribeAction: ActionHandler<ISubscribePayload> = ({ commit, state }, { payload }) => {
	if(!state.status.loading) {
		commit({
			type: 'setLoading',
			payload: true
		})
		api.subscribe({
			...payload,
			handlerUrl: state.config.subscribeUrl
		})
		.then(result => {
			if(process.env.NODE_ENV === "development")
			{
				setTimeout(() => {
					commit({
						type: 'setState',
						payload: {
							status: {
								...state.status,
								code: result,
								loading: false
							}
						}
					})
				}, 1000 * 5);
			} else {
				commit({
					type: 'setState',
					payload: {
						status: {
							...state.status,
							code: result,
							loading: false
						}
					}
				})
			}
		})
		.catch(() => {
			commit({
				type: 'setState',
				payload: {
					status: {
						...state.status,
						code: Status.NOT_OK,
						loading: false
					}
				}
			})
		})
	}
}

const unsubscribeAction: ActionHandler<undefined> = ({ commit, state }) => {
	if(!state.status.loading) {
		commit({
			type: 'setLoading',
			payload: true
		})
		api.unsubscribe(state.config.unsubscribe.unsubscribedUrl)
			.then(status => {
				commit({
					type: 'setState',
					payload: {
						status: {
							...state.status,
							code: status,
							loading: false
						}
					}
				})
			})
			.catch(unsubscribeError => {
				console.error({
					unsubscribeError
				})
				commit({
					type: 'setState',
					payload: {
						status: {
							...state.status,
							code: Status.NOT_OK,
							loading: false
						}
					}
				})
			})
	}
}

const actions: ActionTree<IState, IState> = {
	initApp: initAppAction,
	subscribe: subscribeAction,
	unsubscribe: unsubscribeAction
}

export default actions;
